import { PlanetConfig } from '../types/planet';

/**
 * 太阳系行星参数配置
 * 半径和距离已做可视化缩放，兼顾美观与可辨识性
 *
 * 纹理来源：Solar System Scope (https://www.solarsystemscope.com/textures/)
 * 已下载到本地 /textures/，免费用于非商业项目
 */
export const PLANET_CONFIGS: PlanetConfig[] = [
  {
    name: 'Sun',
    nameCn: '太阳',
    radius: 2.0,
    realRadius: 696340,
    orbitRadius: 0,
    orbitEccentricity: 0,
    yearPeriod: 0,
    rotationPeriod: 609.12,
    axialTilt: 7.25,
    color: 0xffcc00,
    emissive: 0xff8800,
    textureUrl: '/textures/2k_sun.jpg',
    info: '太阳是一颗G型主序星，核心通过核聚变每秒将约6亿吨氢转化为氦。光从核心到表面需要约10万-17万年。',
    mass: 333000,        // 地球质量的倍数
    temperature: 5500,    // 表面温度（°C）
  },
  {
    name: 'Mercury',
    nameCn: '水星',
    radius: 0.15,
    realRadius: 2439.7,
    orbitRadius: 8,
    orbitEccentricity: 0.2056,
    yearPeriod: 87.97,
    rotationPeriod: 1407.6,
    axialTilt: 0.034,
    color: 0xb5b5b5,
    textureUrl: '/textures/2k_mercury.jpg',
    info: '水星是离太阳最近的行星，也是太阳系最小的行星。昼夜温差极大，白天可达430°C，夜间低至-180°C。',
    mass: 0.055,
    gravity: 0.38,
    temperature: 167,
    moons: 0,
  },
  {
    name: 'Venus',
    nameCn: '金星',
    radius: 0.38,
    realRadius: 6051.8,
    orbitRadius: 11,
    orbitEccentricity: 0.0068,
    yearPeriod: 224.7,
    rotationPeriod: -5832.5, // 逆向自转
    axialTilt: 177.4,
    color: 0xe8cda0,
    textureUrl: '/textures/2k_venus_surface.jpg',
    info: '金星是太阳系最热的行星（表面温度约465°C），大气层主要由二氧化碳组成，产生强烈的温室效应。',
    mass: 0.815,
    gravity: 0.9,
    temperature: 465,
    moons: 0,
  },
  {
    name: 'Earth',
    nameCn: '地球',
    radius: 0.4,
    realRadius: 6371,
    orbitRadius: 15,
    orbitEccentricity: 0.0167,
    yearPeriod: 365.25,
    rotationPeriod: 23.93,
    axialTilt: 23.44,
    color: 0x4488ff,
    textureUrl: '/textures/2k_earth_daymap.jpg',
    info: '地球是太阳系中唯一已知存在生命的行星。拥有液态水、适宜的大气层和磁场保护。',
    mass: 1,
    gravity: 1,
    temperature: 15,
    moons: 1,
  },
  {
    name: 'Mars',
    nameCn: '火星',
    radius: 0.28,
    realRadius: 3389.5,
    orbitRadius: 20,
    orbitEccentricity: 0.0934,
    yearPeriod: 687,
    rotationPeriod: 24.62,
    axialTilt: 25.19,
    color: 0xcc5533,
    textureUrl: '/textures/2k_mars.jpg',
    info: '火星被称为"红色行星"，表面富含氧化铁。拥有太阳系最高的火山——奥林匹斯山，高度达21.9公里。',
    mass: 0.107,
    gravity: 0.38,
    temperature: -65,
    moons: 2,
  },
  {
    name: 'Jupiter',
    nameCn: '木星',
    radius: 1.2,
    realRadius: 69911,
    orbitRadius: 30,
    orbitEccentricity: 0.0489,
    yearPeriod: 4333,
    rotationPeriod: 9.93,
    axialTilt: 3.13,
    color: 0xd4a574,
    textureUrl: '/textures/2k_jupiter.jpg',
    info: '木星是太阳系最大的行星，质量是其余行星总质量的2.5倍。大红斑是一个持续数百年的巨大风暴。',
    mass: 317.8,
    gravity: 2.53,
    temperature: -110,
    moons: 95,
  },
  {
    name: 'Saturn',
    nameCn: '土星',
    radius: 1.0,
    realRadius: 58232,
    orbitRadius: 40,
    orbitEccentricity: 0.0565,
    yearPeriod: 10759,
    rotationPeriod: 10.66,
    axialTilt: 26.73,
    color: 0xe8d5a3,
    hasRing: true,
    ringInner: 1.5,
    ringOuter: 2.5,
    textureUrl: '/textures/2k_saturn.jpg',
    info: '土星以其壮丽的光环系统闻名。光环主要由冰粒和岩石碎片组成，厚度仅约10米。',
    mass: 95.2,
    gravity: 1.07,
    temperature: -140,
    moons: 146,
  },
  {
    name: 'Uranus',
    nameCn: '天王星',
    radius: 0.7,
    realRadius: 25362,
    orbitRadius: 50,
    orbitEccentricity: 0.0457,
    yearPeriod: 30687,
    rotationPeriod: -17.24,
    axialTilt: 97.77,
    color: 0x88ccdd,
    textureUrl: '/textures/2k_uranus.jpg',
    info: '天王星是太阳系中唯一"躺着"公转的行星，自转轴几乎与公转轨道平行。',
    mass: 14.5,
    gravity: 0.89,
    temperature: -195,
    moons: 28,
  },
  {
    name: 'Neptune',
    nameCn: '海王星',
    radius: 0.68,
    realRadius: 24622,
    orbitRadius: 58,
    orbitEccentricity: 0.0113,
    yearPeriod: 60190,
    rotationPeriod: 16.11,
    axialTilt: 28.32,
    color: 0x3366ff,
    textureUrl: '/textures/2k_neptune.jpg',
    info: '海王星是太阳系最远的行星，拥有太阳系最强的风暴，风速可达2100 km/h。',
    mass: 17.1,
    gravity: 1.14,
    temperature: -200,
    moons: 16,
  },
];

/** 获取行星配置 */
export function getPlanetConfig(name: string): PlanetConfig | undefined {
  return PLANET_CONFIGS.find(p => p.name.toLowerCase() === name.toLowerCase());
}
